import { CASE_STUDIES_ENABLED } from "@/config/features";
import { useLanguage } from "@/i18n/LanguageContext";
import { Linkedin, Instagram } from "lucide-react";
import { Link } from "react-router-dom";
import logoWhite from "@/assets/beedigit_logo_black_long.svg";
import { openCookiePreferences } from "@/components/CookieConsent";

import { useModules } from "@/hooks/useCatalog";
import { useIndustryList } from "@/hooks/useIndustryList";

const staticCols = (
  modules: { slug: string; title: string }[],
  industries: { slug: string; title: string }[],
) => [
  {
    title: "Platform",
    links: [
      { to: "/platform", label: "Overview" },
      { to: "/hardware", label: "Hardware" },
      { to: "/integrations", label: "Integrations" },
      { to: "/pricing", label: "Pricing / Start Small" },
      { to: "/roi-calculator", label: "ROI Calculator" },
      { to: "/siri", label: "INCIT SIRI Certification" },
    ],
  },
  {
    title: "Modules",
    links: modules.slice(0, 8).map((m) => ({
      to: `/modules/${m.slug}`,
      label: m.title.split(" — ")[0],
    })),
  },
  {
    title: "Industries",
    links: industries.map((i) => ({ to: `/industries/${i.slug}`, label: i.title })),
  },
  {
    title: "Resources",
    links: [
      { to: "/blog", label: "Blog" },
  ...(CASE_STUDIES_ENABLED ? [{ to: "/case-studies", label: "Case Studies" }] : []),
      { to: "/news", label: "News" },
      { to: "/glossary", label: "Glossary" },
      { to: "/lean", label: "Lean & CI" },
      { to: "/about", label: "About" },
      { to: "/partners", label: "Partners" },
      { to: "/contact", label: "Contact" },
    ],
  },
];

const Footer = () => {
  const { t, localizedPath } = useLanguage();
  const year = new Date().getFullYear();
  const cols = staticCols(useModules().modules, useIndustryList());
  return (
    <footer className="border-t border-border bg-background">
      <div className="container mx-auto px-4 py-12">
        <div className="grid gap-10 lg:grid-cols-[1.2fr_repeat(4,1fr)]">
          <div className="space-y-3">
            <img src={logoWhite} alt={t("BEEDIGIT manufacturing platform logo")} className="h-8" />
            <p className="text-sm text-muted-foreground max-w-xs">
              {t("Plug & Play MES, OEE and IIoT platform. Industrial hardware, SaaS and industry templates — ready to use from day one.")}
            </p>
            <p className="text-xs text-muted-foreground">{t("HQ: Dubai Internet City, UAE")}</p>
            <div className="flex items-center gap-3 pt-2">
              <a href="https://www.linkedin.com/company/beedigit" target="_blank" rel="noopener noreferrer" aria-label={t("LinkedIn")} className="text-muted-foreground hover:text-primary transition-colors">
                <Linkedin className="h-5 w-5" />
              </a>
              <a href="https://www.instagram.com/beedigit_mes" target="_blank" rel="noopener noreferrer" aria-label={t("Instagram")} className="text-muted-foreground hover:text-primary transition-colors">
                <Instagram className="h-5 w-5" />
              </a>
            </div>
          </div>

          {cols.map((c) => (
            <div key={c.title}>
              <p className="eyebrow mb-3">{t(c.title)}</p>
              <ul className="space-y-2">
                {c.links.map((l) => (
                  <li key={l.to}>
                    <Link to={localizedPath(l.to)} className="text-sm text-foreground/80 hover:text-primary transition-colors">{t(l.label)}</Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-10 pt-6 border-t border-border flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-xs text-muted-foreground">
          <p>© {year} {t("BEEDIGIT. All rights reserved.")}</p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <Link to={localizedPath("/privacy")} className="hover:text-primary transition-colors">{t("Privacy Notice")}</Link>
            <Link to={localizedPath("/terms")} className="hover:text-primary transition-colors">{t("Terms of Use")}</Link>
            <button type="button" onClick={openCookiePreferences} className="hover:text-primary transition-colors">
              {t("Cookie preferences")}
            </button>
            <span>{t("Plug & Play MES · OEE · IIoT · EnMS · QMS · CMMS")}</span>
          </div>
        </div>

      </div>
    </footer>
  );
};

export default Footer;
